import {
  ArrowLeftRight,
  BarChart2,
  FileText,
  Mic,
  Plus,
} from 'lucide-react'
import {
  CONFIDENCE_META,
  formatValue,
  type Assumption,
  type DerivationInputs,
  type Reconcile,
  type Source,
} from './sizingView'

interface Props {
  derivation: DerivationInputs
  reconcile?: Reconcile | null
  unit: 'currency' | 'percent'
}

export function DerivationPanel({ derivation, reconcile, unit }: Props) {
  return (
    <div className="px-4 pb-4 pt-1">
      <div
        className="grid gap-x-8 gap-y-6 rounded-[14px] px-5 py-5"
        style={{
          gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))',
          backgroundColor: 'rgba(255,255,255,0.55)',
        }}
      >
        <section>
          <SectionLabel>Derivation</SectionLabel>
          <pre
            className="mt-2 whitespace-pre-wrap rounded-xl px-4 py-3 font-mono text-[12px] leading-relaxed"
            style={{
              backgroundColor: 'white',
              color: 'var(--color-text)',
            }}
          >
            {derivation.formula}
          </pre>
          {derivation.alternateLabel ? (
            <button
              type="button"
              className="mt-3 inline-flex items-center gap-1 text-[11px] transition-colors hover:text-[var(--color-market)]"
              style={{ color: 'var(--color-text-secondary)' }}
              title="Switch derivation approach"
            >
              <ArrowLeftRight className="h-3 w-3" />
              Switch: {derivation.alternateLabel}
            </button>
          ) : null}
        </section>

        <section>
          <SectionLabel>Assumptions</SectionLabel>
          <ul className="mt-2 space-y-1.5">
            {derivation.assumptions.map((a) => (
              <AssumptionRow key={a.id} assumption={a} />
            ))}
            {derivation.assumptions.length === 0 ? (
              <li
                className="text-[11px] italic"
                style={{ color: 'var(--color-text-muted)' }}
              >
                No assumptions recorded yet.
              </li>
            ) : null}
          </ul>
        </section>

        <section>
          <SectionLabel>Sources</SectionLabel>
          <ul className="mt-2 space-y-1">
            {derivation.sources.map((s) => (
              <SourceRow key={s.id} source={s} />
            ))}
          </ul>
          <button
            type="button"
            className="mt-2 inline-flex items-center gap-1 text-[11px] transition-colors hover:text-[var(--color-market)]"
            style={{ color: 'var(--color-business-600)' }}
          >
            <Plus className="h-3 w-3" />
            link evidence
          </button>
        </section>
      </div>

      {reconcile ? <ReconcileStrip reconcile={reconcile} unit={unit} /> : null}
    </div>
  )
}

function SectionLabel({ children }: { children: React.ReactNode }) {
  return (
    <span
      className="text-[10px] font-semibold uppercase tracking-[0.1em]"
      style={{ color: 'var(--color-text-muted)' }}
    >
      {children}
    </span>
  )
}

function AssumptionRow({ assumption }: { assumption: Assumption }) {
  const meta = CONFIDENCE_META[assumption.confidence]
  return (
    <li className="flex items-baseline justify-between gap-3">
      <span className="text-[12px]" style={{ color: 'var(--color-text)' }}>
        {assumption.label}
      </span>
      <span className="flex items-center gap-2">
        <span
          className="font-mono text-[12px] tabular-nums"
          style={{ color: 'var(--color-text)' }}
        >
          {assumption.value}
        </span>
        <span
          className="inline-block h-[6px] w-[6px] rounded-full"
          style={{ backgroundColor: meta.dot }}
          title={meta.label}
        />
      </span>
    </li>
  )
}

function SourceRow({ source }: { source: Source }) {
  const Icon =
    source.kind === 'interview' ? Mic : source.kind === 'benchmark' ? BarChart2 : FileText
  return (
    <li className="flex items-center gap-2 text-[12px]" style={{ color: 'var(--color-text)' }}>
      <Icon className="h-3 w-3 shrink-0" style={{ color: 'var(--color-text-muted)' }} />
      <button
        type="button"
        className="truncate text-left transition-colors hover:text-[var(--color-business-700)]"
      >
        {source.label}
      </button>
    </li>
  )
}

function ReconcileStrip({
  reconcile,
  unit,
}: {
  reconcile: Reconcile
  unit: 'currency' | 'percent'
}) {
  const fg = reconcile.withinTolerance ? '#157243' : '#9a5211'
  const bg = reconcile.withinTolerance ? '#e0f3e8' : '#fbead3'
  return (
    <div
      className="mt-3 flex items-center justify-between gap-3 rounded-xl px-4 py-3 text-[12px]"
      style={{ backgroundColor: bg, color: fg }}
    >
      <div className="flex items-center gap-2">
        <span className="font-semibold tracking-wide">Reconcile</span>
        <span>
          Top-down {formatValue(reconcile.topDown, unit)} ≈ bottom-up{' '}
          {formatValue(reconcile.bottomUp, unit)} · {reconcile.diffPct}% gap
        </span>
      </div>
      <button
        type="button"
        className="inline-flex items-center gap-1 font-medium transition-opacity hover:opacity-80"
      >
        view diff →
      </button>
    </div>
  )
}
